import { Trash2 } from 'lucide-react';

import { Button } from '@/components/Button/Button';

import type { EditableRubricUnit } from '../editorTypes';
import styles from './RubricUnitRow.module.css';

interface RubricUnitRowProps {
  disabled?: boolean;
  index: number;
  onChange: (updates: Partial<EditableRubricUnit>) => void;
  onRemove: () => void;
  unit: EditableRubricUnit;
  canRemove: boolean;
}

export function RubricUnitRow({
  canRemove,
  disabled = false,
  index,
  onChange,
  onRemove,
  unit,
}: RubricUnitRowProps) {
  const nameId = `${unit.id}-name`;
  const detailsId = `${unit.id}-details`;
  const scoreId = `${unit.id}-score`;
  const hasNameError = unit.name.trim().length === 0;
  const hasScoreError = !Number.isFinite(unit.score) || unit.score <= 0;

  return (
    <li className={styles.row}>
      <div className={styles.header}>
        <p className={styles.title}>Rubric unit {index + 1}</p>
        <Button
          aria-label={`Remove rubric unit ${index + 1}`}
          destructive
          disabled={disabled || !canRemove}
          iconOnly
          onClick={onRemove}
          size="small"
          title={!canRemove ? 'At least one rubric unit is required.' : undefined}
          variant="ghost"
        >
          <Trash2 aria-hidden="true" size={16} strokeWidth={2} />
        </Button>
      </div>

      <div className={styles.grid}>
        <label className={styles.field} htmlFor={nameId}>
          <span className={styles.label}>Name</span>
          <input
            aria-invalid={hasNameError ? 'true' : undefined}
            className={styles.input}
            disabled={disabled}
            id={nameId}
            onChange={(event) => {
              onChange({ name: event.target.value });
            }}
            value={unit.name}
          />
        </label>

        <label className={styles.field} htmlFor={scoreId}>
          <span className={styles.label}>Points</span>
          <input
            aria-invalid={hasScoreError ? 'true' : undefined}
            className={styles.input}
            disabled={disabled}
            id={scoreId}
            min="0"
            onChange={(event) => {
              onChange({
                score:
                  event.target.value === ''
                    ? 0
                    : Number(event.target.value),
              });
            }}
            step="0.25"
            type="number"
            value={Number.isFinite(unit.score) ? unit.score : ''}
          />
        </label>
      </div>

      <label className={styles.field} htmlFor={detailsId}>
        <span className={styles.label}>Instructions</span>
        <textarea
          className={styles.textarea}
          disabled={disabled}
          id={detailsId}
          onChange={(event) => {
            onChange({ details: event.target.value });
          }}
          rows={3}
          value={unit.details}
        />
      </label>
    </li>
  );
}
